import React from 'react';
import { motion } from 'framer-motion';
import { Check, Info, Quote } from 'lucide-react';
import { useGclid } from '../src/hooks/useGclid';
import { buildShopUrl, trackCTAClick } from '../src/utils/tracking';
const SHOP_URL = 'https://hunderunde.shop/products/futtertopping-magen-darm';
export function ProductInfo() {
  const gclid = useGclid();
  const href = buildShopUrl(SHOP_URL, gclid);
  return <section className="w-full px-4 py-12 max-w-3xl mx-auto space-y-16" data-id="element-114">
      {/* Product Explanation */}
      <motion.div initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} className="space-y-6" data-id="element-115">
        <h3 className="font-handwriting text-4xl text-forest font-bold text-center" data-id="element-116">
          Was die Darmflora braucht – und warum Präbiotika der Schlüssel sind
        </h3>

        <p className="font-serif text-lg text-gray-700 text-center mb-8" data-id="element-117">
          Der Unterschied: Probiotika sind lebende Bakterien.{' '}
          <strong className="text-forest" data-id="element-118">Präbiotika</strong> sind die Nahrung,
          die diese nützlichen Bakterien brauchen, um sich im Darm anzusiedeln
          und zu vermehren.
        </p>

        <div className="grid gap-4 md:grid-cols-2" data-id="element-119">
          {[{
          title: 'Inulin',
          desc: 'Nahrung für nützliche Darmbakterien'
        }, {
          title: 'Topinambur',
          desc: 'Natürliches Präbiotikum, fördert Bifidobakterien'
        }, {
          title: 'Mariendistel',
          desc: 'Schützt die Leber während des Wiederaufbaus'
        }, {
          title: 'Leinsamen',
          desc: 'Beruhigt gereizte Darmwände'
        }, {
          title: 'Artischocke',
          desc: 'Unterstützt Verdauungsenzyme'
        }, {
          title: '100% Pflanzlich',
          desc: 'Keine künstlichen Zusätze'
        }].map((item, i) => <div key={i} className="flex items-start gap-3 bg-white/50 p-4 rounded-xl border border-sage" data-id="element-120">
              <div className="mt-1 bg-sage text-forest rounded-full p-1" data-id="element-121">
                <Check size={16} strokeWidth={3} data-id="element-122" />
              </div>
              <div data-id="element-123">
                <h4 className="font-serif font-bold text-forest" data-id="element-124">
                  {item.title}
                </h4>
                <p className="font-serif text-sm text-gray-600" data-id="element-125">{item.desc}</p>
              </div>
            </div>)}
        </div>

        {/* CTA #2 */}
        <div className="pt-8 text-center" data-id="element-126">
          <a href={href} onClick={() => trackCTAClick('Jetzt für meinen Hund bestellen', 'ingredients_cta')} target="_blank" rel="noopener noreferrer" className="inline-block w-full md:w-auto bg-forest text-cream font-serif font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:scale-105 hover:bg-forest/90 transition-all duration-300" data-id="element-127">
            Jetzt für meinen Hund bestellen
          </a>
        </div>
      </motion.div>

      {/* Did You Know */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} className="bg-sage/40 rounded-2xl p-6 md:p-8 border border-sage relative overflow-hidden" data-id="element-128">
        <div className="absolute top-0 right-0 p-4 opacity-10 text-forest" data-id="element-129">
          <Info size={100} data-id="element-130" />
        </div>
        <h3 className="font-handwriting text-3xl text-forest font-bold mb-6 relative z-10" data-id="element-131">
          Wusstest du das?
        </h3>
        <ul className="space-y-4 font-serif text-lg text-gray-800 relative z-10" data-id="element-132">
          <li className="flex items-center gap-3" data-id="element-133">
            <span className="w-2 h-2 rounded-full bg-brown" data-id="element-134"></span>
            70% des Immunsystems sitzen im Darm
          </li>
          <li className="flex items-center gap-3" data-id="element-135">
            <span className="w-2 h-2 rounded-full bg-brown" data-id="element-136"></span>
            Antibiotika zerstören auch nützliche Bakterien
          </li>
          <li className="flex items-center gap-3" data-id="element-137">
            <span className="w-2 h-2 rounded-full bg-brown" data-id="element-138"></span>
            Der Wiederaufbau der Darmflora kann Monate dauern – ohne
            Unterstützung
          </li>
        </ul>
      </motion.div>

      {/* Expert Quote */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="text-center px-4 md:px-12" data-id="element-139">
        <Quote className="mx-auto text-brown/30 mb-4" size={48} data-id="element-140" />
        <p className="font-serif text-xl md:text-2xl italic text-gray-700 leading-relaxed mb-6" data-id="element-141">
          "Nach Antibiotika-Behandlungen ist der Wiederaufbau der Darmflora
          essentiell. Präbiotika wie Inulin und Topinambur liefern die
          Nahrungsgrundlage für nützliche Bakterien und beschleunigen den
          natürlichen Regenerationsprozess."
        </p>
        <p className="font-serif font-bold text-forest text-lg" data-id="element-142">
          – Dr. Nane Schomburg, Tierärztin
        </p>
      </motion.div>
    </section>;
}